// Dark Mode Design System Configuration
import { TYPOGRAPHY_CONFIG } from '../config/typography.config';

export const darkTheme = {
  // Color Palette
  colors: {
    // Primary colors
    primary: {
      50: '#EDE9FE',
      100: '#DCD7FE',
      200: '#CABFFD',
      300: '#AC94FA',
      400: '#9061F9',
      500: '#7E3AF2', // Main purple
      600: '#6C2BD9',
      700: '#5521B5',
      800: '#4A1D96',
      900: '#3E1878',
    },
    
    // Dark backgrounds
    dark: {
      900: '#111827', // Darkest
      800: '#1F2937',
      700: '#374151',
      600: '#4B5563',
      500: '#6B7280',
      400: '#9CA3AF',
    },
    
    // Accent colors
    accent: {
      blue: '#3B82F6',
      green: '#10B981', 
      yellow: '#F59E0B',
      red: '#EF4444',
      purple: '#8B5CF6',
      teal: '#14B8A6',
      pink: '#EC4899',
    },
    
    // Neutral colors
    neutral: {
      white: '#FFFFFF',
      gray: {
        50: '#F9FAFB',
        100: '#F3F4F6',
        200: '#E5E7EB',
        300: '#D1D5DB',
        400: '#9CA3AF',
        500: '#6B7280',
        600: '#4B5563',
        700: '#374151',
        800: '#1F2937',
        900: '#111827',
      }
    },
    
    // Status colors
    status: {
      success: '#059669',
      warning: '#D97706',
      error: '#DC2626',
      info: '#0891B2',
    }
  },
  
  // Typography - Updated with minimum 14px standard
  typography: {
    fontFamily: "'IranYekanX', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: {
      xs: '0.75rem',     // 12px - ONLY for metadata/timestamps
      sm: '0.875rem',    // 14px - Minimum readable font size
      base: '1rem',      // 16px - Default body text
      lg: '1.125rem',    // 18px - Section headers
      xl: '1.25rem',     // 20px - Important values
      '2xl': '1.5rem',   // 24px - Page sections
      '3xl': '1.875rem', // 30px - Main headings
      '4xl': '2.25rem',  // 36px - KPI values
      '5xl': '3rem',     // 48px - Hero numbers
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    lineHeight: {
      xs: 1.2,
      sm: 1.4,
      base: 1.5,
      lg: 1.6,
      xl: 1.75,
    }
  },
  
  // Spacing
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
    '2xl': '4rem',
  },
  
  // Border radius
  borderRadius: {
    none: '0',
    sm: '0.125rem',
    base: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    full: '9999px',
  },
  
  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    none: 'none',
  },
  
  // Animation
  transitions: {
    fast: '150ms',
    base: '300ms',
    slow: '500ms',
    slower: '700ms',
  }
};

// Chart colors optimized for dark mode
export const chartColors = {
  primary: ['#8B5CF6', '#6366F1', '#3B82F6', '#0891B2', '#10B981', '#059669'],
  secondary: ['#EC4899', '#F43F5E', '#EF4444', '#F59E0B', '#F97316', '#DC2626'],
  gradient: {
    purple: ['#8B5CF6', '#A78BFA'],
    blue: ['#3B82F6', '#60A5FA'],
    green: ['#10B981', '#34D399'],
    orange: ['#F59E0B', '#FCD34D'],
  }
};

// Enhanced colors for better visibility and modern look
export const enhancedColors = {
  primary: [
    '#8B5CF6', // Purple
    '#3B82F6', // Blue  
    '#10B981', // Green
    '#F59E0B', // Orange
    '#EF4444', // Red
    '#EC4899', // Pink
    '#06B6D4', // Cyan
    '#84CC16', // Lime
    '#F97316', // Orange alt
    '#8B5A3C'  // Brown
  ],
  secondary: [
    '#A78BFA', // Light Purple
    '#60A5FA', // Light Blue
    '#34D399', // Light Green
    '#FBBF24', // Light Orange
    '#F87171', // Light Red
    '#F472B6'  // Light Pink
  ],
  neutral: [
    '#F9FAFB', // Very Light Gray
    '#F3F4F6', // Light Gray
    '#E5E7EB', // Gray
    '#D1D5DB', // Medium Gray
    '#9CA3AF', // Dark Gray
    '#6B7280'  // Very Dark Gray
  ]
};

// Component styles
export const componentStyles = {
  card: `
    bg-gray-800 
    border border-gray-700 
    rounded-lg 
    shadow-lg 
    transition-all 
    duration-300 
    hover:shadow-xl 
    hover:border-gray-600
  `,
  
  button: {
    primary: `
      bg-purple-600 
      text-white 
      px-4 py-2 
      rounded-lg 
      font-medium 
      transition-all 
      duration-300 
      hover:bg-purple-700 
      hover:shadow-lg 
      active:scale-95
    `,
    secondary: `
      bg-gray-700 
      text-gray-200 
      px-4 py-2 
      rounded-lg 
      font-medium 
      transition-all 
      duration-300 
      hover:bg-gray-600 
      hover:shadow-lg 
      active:scale-95
    `,
  },
  
  input: `
    bg-gray-700 
    border border-gray-600 
    text-white 
    rounded-lg 
    px-3 py-2 
    transition-all 
    duration-300 
    focus:border-purple-500 
    focus:ring-2 
    focus:ring-purple-500 
    focus:outline-none
  `,
  
  select: {
    control: (theme: any) => ({
      ...theme,
      backgroundColor: darkTheme.colors.dark[700],
      borderColor: darkTheme.colors.dark[600],
      color: darkTheme.colors.neutral.white,
      '&:hover': {
        borderColor: darkTheme.colors.primary[500],
      }
    }),
    menu: (theme: any) => ({
      ...theme,
      backgroundColor: darkTheme.colors.dark[800],
    }),
    option: (theme: any, state: any) => ({
      ...theme,
      backgroundColor: state.isSelected 
        ? darkTheme.colors.primary[600] 
        : state.isFocused 
        ? darkTheme.colors.dark[700] 
        : darkTheme.colors.dark[800],
      color: darkTheme.colors.neutral.white,
      '&:hover': {
        backgroundColor: darkTheme.colors.dark[700],
      }
    }),
  }
};

// Cyber Intelligence Theme has been removed

// Smart Light Theme - Enhanced and refined version inspired by smart-sso colors
export const lightIntelligenceTheme = {
  name: 'Smart Light',
  colors: {
    primary: {
      50: '#F8FEEA', // Refined lighter shade for subtle backgrounds - less saturated
      100: '#EFFBC7', // Very light for hover states - more harmonious
      200: '#DEF788', // Light shade for active backgrounds - softer tone
      300: '#D0EF4B', // Medium-light for buttons - refined
      400: '#C0F731', // Main brand color from smart-sso - maintained
      500: '#B3E72C', // Slightly adjusted main color for better balance with UI
      600: '#A3D329', // Slightly darker for hover states - more balanced
      700: '#8EB825', // Darker for active states - refined
      800: '#779A20', // Dark for text on light backgrounds - improved contrast
      900: '#60791B', // Very dark for high contrast text - better readability
      950: '#4A5C15', // Extra dark for special cases - enhanced contrast
    },
    
    secondary: {
      50: '#E7F9ED', // Lighter shade for subtle backgrounds - less saturated
      100: '#D0F2DC', // Very light for hover states - more harmonious
      200: '#A7E5BC', // Light shade for active backgrounds - more professional
      300: '#71D394', // Medium-light for buttons - refined
      400: '#38BC6A', // Medium shade - more balanced with primary
      500: '#10BD25', // Success green from smart-sso - maintained
      600: '#0EA522', // Slightly darker for hover states - refined
      700: '#0C8D1F', // Darker for active states - refined
      800: '#09731A', // Dark for text on light backgrounds - improved contrast
      900: '#075D15', // Very dark for high contrast text - better readability
      950: '#054A11', // Extra dark for special cases - enhanced contrast
    },
    
    
    // Enhanced background colors - Light theme with subtle gradients to match smart-sso
    background: {
      primary: '#FFFFFF',
      secondary: '#F7F9FA', // Adjusted to match smart-sso
      tertiary: '#F0F2F5', // Adjusted to match smart-sso
      card: '#FFFFFF',
      elevated: '#FFFFFF',
      overlay: 'rgba(255, 255, 255, 0.92)',
      glass: 'rgba(255, 255, 255, 0.85)',
      subtle: '#F9FAFC', // Very subtle tint for depth to match smart-sso
      highlight: 'rgba(192, 247, 49, 0.08)', // Brand color highlight adjusted to match smart-sso
      active: 'rgba(192, 247, 49, 0.15)', // For active states
      hover: 'rgba(192, 247, 49, 0.05)', // For hover states
    },
    
    // Enhanced text colors with better contrast ratios and warmth - optimized for accessibility
    text: {
      primary: '#18181B', // Darkest text for primary content - enhanced for 7:1 contrast ratio
      secondary: '#27272A', // Enhanced dark gray with better contrast - 5:1 ratio
      tertiary: '#3F3F46', // Improved medium gray for better readability - 4.5:1 ratio
      quaternary: '#52525B', // Light gray with adequate contrast - 3:1 ratio
      muted: '#71717A', // Muted text with accessible contrast
      placeholder: '#A1A1AA', // Light gray for placeholders that meets AA standards
      inverse: '#FFFFFF', // White text for dark elements
      brand: '#556B18', // Brand color for text - darker for enhanced readability (4.5:1 on white)
      success: '#046C15', // Success text color - enhanced for better contrast
      warning: '#9A3412', // Warning text color - enhanced for better contrast
      error: '#9F1239', // Error text color - enhanced for better contrast
      info: '#1E40AF', // Info text color - meets accessibility standards
      subtle: '#71717A', // For less important text that still meets AA standards
      link: '#1D4ED8', // For link text - meets AAA standards on white backgrounds
      linkHover: '#1E40AF', // For link hover states
      emphasis: '#000000', // For extra emphasis when needed
    },
    
    // Enhanced border colors with better visibility and distinction
    border: {
      primary: '#E5E7EB', // Slightly warmer light border for better visibility
      secondary: '#D1D5DB', // Enhanced lighter border
      tertiary: '#9CA3AF', // Improved medium border
      accent: '#C0F731', // Primary color from smart-sso - adjusted
      focus: '#10BD25', // Secondary color from smart-sso - adjusted
      hover: '#AFE02C', // Darker primary for hover states
      active: '#9CC827', // Even darker for active states
      transparent: 'transparent',
      input: '#D1D5DB', // Border for form elements
      inputFocus: '#9CC827', // Focus border for form elements - adjusted
      divider: '#F3F4F6', // Very subtle divider
    },
    
    // Enhanced neutral palette with better gradation and warmth
    neutral: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
      950: '#0D1323', // Extra dark for special cases
    },
    
    // Enhanced accent colors with better visibility and coordination
    accentColors: {
      cyan: '#06B6D4',
      purple: '#8B5CF6',
      green: '#10BD25', // From smart-sso - adjusted to match image
      orange: '#F97316',
      pink: '#EC4899',
      yellow: '#FBBF24',
      indigo: '#6366F1',
      lime: '#C0F731', // From smart-sso - adjusted to match image
      emerald: '#10B981', // Additional accent - brighter
      blue: '#3B82F6', // Additional accent - brighter
      red: '#EF4444', // Additional accent
    },
    
    // Enhanced status colors with better contrast and coordination - adjusted to match smart-sso
    status: {
      success: '#10BD25', // Adjusted to match smart-sso
      warning: '#F59E0B', // Adjusted for better contrast
      error: '#EF4444', // Adjusted for better contrast
      info: '#3B82F6', // Adjusted for better contrast
      successLight: 'rgba(16, 189, 37, 0.12)', // Background variant - adjusted
      warningLight: 'rgba(245, 158, 11, 0.12)', // Background variant
      errorLight: 'rgba(239, 68, 68, 0.12)', // Background variant
      infoLight: 'rgba(59, 130, 246, 0.12)', // Background variant
      successBorder: 'rgba(16, 189, 37, 0.25)', // Border variant - adjusted
      warningBorder: 'rgba(245, 158, 11, 0.25)', // Border variant
      errorBorder: 'rgba(239, 68, 68, 0.25)', // Border variant
      infoBorder: 'rgba(59, 130, 246, 0.25)', // Border variant
    },
    
    // Utility colors
    transparent: 'transparent',
    white: '#FFFFFF',
    black: '#000000',
  },
  
  typography: {
    fontFamily: "'IranYekanX', 'AlibabaSans', sans-serif", // Matched to smart-sso
    fontSize: {
      xs: '12px',        // ONLY for metadata/timestamps - use sparingly
      sm: '14px',        // Minimum readable font size
      base: '16px',      // Default body text
      lg: '18px',        // Section headers
      xl: '20px',        // Important values
      '2xl': '24px',     // Page sections
      '3xl': '30px',     // Main headings
      '4xl': '36px',     // KPI values
      '5xl': '48px',     // Hero numbers
      '6xl': '60px',     // Extra large displays
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
      black: 900,
    },
    lineHeight: {
      none: '1',
      tight: '1.25',
      snug: '1.375',
      normal: '1.5',
      relaxed: '1.625',
      loose: '2',
    },
    letterSpacing: {
      tighter: '-0.05em',
      tight: '-0.025em',
      normal: '0',
      wide: '0.025em',
      wider: '0.05em',
      widest: '0.1em',
    }
  },
  
  spacing: {
    xs: '8px',
    sm: '16px',
    md: '24px',
    lg: '32px',
    xl: '40px',
    '2xl': '48px',
    '3xl': '64px',
    '4xl': '80px',
    '5xl': '96px',
    '6xl': '128px',
  },
  
  borderRadius: {
    none: '0',
    xs: '2px',
    sm: '4px',
    base: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    '2xl': '32px',
    '3xl': '48px',
    full: '9999px',
    button: '8px',
    card: '12px',
    pill: '9999px',
  },
  
  // Enhanced shadows and elevations with professional UX principles
  shadows: {
    // Subtle layering with improved depth perception - using optical illusion techniques
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 5px -1px rgba(0, 0, 0, 0.02)',
    base: '0 2px 4px 0 rgba(0, 0, 0, 0.05), 0 1px 3px 0 rgba(0, 0, 0, 0.03)',
    md: '0 4px 8px -2px rgba(0, 0, 0, 0.06), 0 2px 5px -1px rgba(0, 0, 0, 0.04)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.02)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.12)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.04)',
    none: 'none',
    
    // Enhanced glow effects with optical science
    glow: '0 0 18px rgba(179, 231, 44, 0.2)',
    glowStrong: '0 0 25px rgba(179, 231, 44, 0.3)',
    innerGlow: 'inset 0 0 15px rgba(179, 231, 44, 0.08)',
    subtleGlow: '0 0 10px rgba(179, 231, 44, 0.1)',
    
    // Semantic shadows with consistent styling
    success: '0 5px 12px rgba(16, 189, 37, 0.2)',
    warning: '0 5px 12px rgba(245, 158, 11, 0.2)',
    error: '0 5px 12px rgba(225, 29, 72, 0.2)',
    info: '0 5px 12px rgba(14, 165, 233, 0.2)',
    
    // Card elevations with improved affordance
    card: '0 2px 8px -1px rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03)', 
    cardHover: '0 8px 20px -4px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    cardActive: '0 4px 12px -2px rgba(179, 231, 44, 0.15), 0 0 0 1px rgba(179, 231, 44, 0.2)',
    cardFocus: '0 0 0 2px rgba(179, 231, 44, 0.4), 0 4px 12px -2px rgba(0, 0, 0, 0.05)',
    // Button shadows optimized for interaction states
    button: '0 2px 4px rgba(0, 0, 0, 0.06)',
    buttonHover: '0 4px 6px rgba(0, 0, 0, 0.1)',
    buttonActive: '0 1px 2px rgba(0, 0, 0, 0.15), inset 0 1px 3px rgba(0, 0, 0, 0.1)',
    buttonDisabled: '0 0 0 1px rgba(0, 0, 0, 0.05)',
    
    // Focus states with enhanced accessibility
    focus: '0 0 0 3px rgba(179, 231, 44, 0.5)',
    focusSuccess: '0 0 0 3px rgba(16, 189, 37, 0.5)',
    focusWarning: '0 0 0 3px rgba(245, 158, 11, 0.5)',
    focusError: '0 0 0 3px rgba(225, 29, 72, 0.5)',
    focusInfo: '0 0 0 3px rgba(14, 165, 233, 0.5)',
    focusInset: 'inset 0 0 0 2px rgba(179, 231, 44, 0.5)',
    
    // Additional contextual shadows
    dropdown: '0 4px 12px -2px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    tooltip: '0 4px 8px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)',
    modal: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 10px 20px -8px rgba(0, 0, 0, 0.15)',
    popover: '0 8px 16px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(0, 0, 0, 0.05)',
  },
  
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    base: '300ms cubic-bezier(0.4, 0, 0.2, 1)', 
    slow: '500ms cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: '600ms cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    spring: '500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    // Additional easings
    easeIn: '300ms cubic-bezier(0.4, 0, 1, 1)',
    easeOut: '300ms cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  
  // Enhanced gradients for light theme - matched to smart-sso
  gradients: {
    primary: 'linear-gradient(135deg, #C0F731 0%, #10BD25 100%)',
    primarySoft: 'linear-gradient(135deg, rgba(192, 247, 49, 0.8) 0%, rgba(16, 189, 37, 0.8) 100%)',
    primaryGlass: 'linear-gradient(135deg, rgba(192, 247, 49, 0.2) 0%, rgba(16, 189, 37, 0.2) 100%)',
    neural: 'linear-gradient(45deg, #C0F731 0%, #10BD25 50%, #3B82F6 100%)',
    data: 'linear-gradient(90deg, #6366F1 0%, #C0F731 100%)',
    energy: 'linear-gradient(120deg, #FBBF24 0%, #F97316 100%)',
    // Direction-based gradients
    toR: 'linear-gradient(90deg, #C0F731, #10BD25)',
    toL: 'linear-gradient(270deg, #C0F731, #10BD25)',
    toT: 'linear-gradient(0deg, #C0F731, #10BD25)',
    toB: 'linear-gradient(180deg, #C0F731, #10BD25)',
    toBR: 'linear-gradient(135deg, #C0F731, #10BD25)',
    toBL: 'linear-gradient(225deg, #C0F731, #10BD25)',
    toTR: 'linear-gradient(45deg, #C0F731, #10BD25)',
    toTL: 'linear-gradient(315deg, #C0F731, #10BD25)',
    // Radial gradients
    radial: 'radial-gradient(circle, #C0F731 0%, #10BD25 100%)',
    radialSoft: 'radial-gradient(circle, rgba(192, 247, 49, 0.7) 0%, rgba(16, 189, 37, 0.7) 100%)',
    // Subtle light theme specific gradients
    subtleBg: 'linear-gradient(180deg, #FFFFFF 0%, #F7F9FA 100%)',
    subtleCard: 'linear-gradient(135deg, #FFFFFF 0%, #F0F2F5 100%)',
    subtleAccent: 'linear-gradient(135deg, rgba(192, 247, 49, 0.05) 0%, rgba(16, 189, 37, 0.05) 100%)',
    glass: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%)',
    frost: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)',
    // Status gradients
    success: 'linear-gradient(135deg, #10BD25 0%, #10B981 100%)',
    warning: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)',
    error: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
    info: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
  },
  
  // Additional element-specific styling for better component design
  elements: {
    // Card component with professional UX enhancements
    card: {
      borderRadius: '12px',
      padding: '20px',
      shadow: '0 2px 8px -1px rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03)',
      shadowHover: '0 8px 20px -4px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      transition: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
      borderColor: 'rgba(0, 0, 0, 0.05)',
      gap: '16px',
      headerPadding: '16px 20px',
      bodyPadding: '20px',
      footerPadding: '16px 20px',
    },
    
    // Button component with accessibility and interaction improvements
    button: {
      // Core styles
      borderRadius: '8px',
      padding: '10px 20px',
      fontSize: `${TYPOGRAPHY_CONFIG.fontSizes.sm}px`,
      fontWeight: 500,
      shadow: '0 2px 4px rgba(0, 0, 0, 0.06)',
      shadowHover: '0 4px 6px rgba(0, 0, 0, 0.1)',
      transition: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
      
      // States
      focusRingOffset: '2px',
      focusRingWidth: '3px',
      focusRingColor: 'rgba(179, 231, 44, 0.5)',
      
      // Sizing variants
      small: {
        padding: '6px 12px',
        fontSize: `${TYPOGRAPHY_CONFIG.fontSizes.xs}px`,
        borderRadius: '6px',
      },
      large: {
        padding: '12px 24px',
        fontSize: `${TYPOGRAPHY_CONFIG.fontSizes.base}px`,
        borderRadius: '10px',
      },
      iconOnly: {
        padding: '8px',
        aspectRatio: '1/1',
      },
    },
    
    // Input component with enhanced usability and accessibility
    input: {
      borderRadius: '8px',
      padding: '10px 16px',
      fontSize: `${TYPOGRAPHY_CONFIG.fontSizes.sm}px`,
      borderColor: '#D1D5DB',
      borderColorFocus: '#B3E72C',
      borderWidth: '1px',
      shadow: 'none',
      shadowFocus: '0 0 0 3px rgba(179, 231, 44, 0.4)',
      transition: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
      placeholderColor: '#9CA3AF',
      errorBorderColor: '#E11D48',
      errorShadowFocus: '0 0 0 3px rgba(225, 29, 72, 0.4)',
      height: '40px',
      paddingWithIcon: '10px 16px 10px 42px',
      iconSize: '20px',
      iconPosition: '16px',
    },
    
    // Modal component with improved depth and interaction patterns
    modal: {
      borderRadius: '16px',
      padding: '24px',
      shadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 10px 20px -8px rgba(0, 0, 0, 0.15)',
      overlay: 'rgba(0, 0, 0, 0.4)',
      transition: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
      headerPadding: '20px 24px',
      bodyPadding: '24px',
      footerPadding: '20px 24px',
      maxWidth: '500px',
      maxHeight: '90vh',
      closeButtonSize: '32px',
      closeButtonPosition: '16px',
    },
    
    // Toast component with enhanced visibility and timing
    toast: {
      borderRadius: '8px',
      padding: '12px 16px',
      shadow: '0 10px 15px -3px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      transition: '300ms cubic-bezier(0, 0, 0.2, 1)',
      maxWidth: '400px',
      defaultDuration: 5000,
      successBg: 'rgba(16, 189, 37, 0.95)',
      errorBg: 'rgba(225, 29, 72, 0.95)',
      infoBg: 'rgba(14, 165, 233, 0.95)',
      warningBg: 'rgba(245, 158, 11, 0.95)',
      zIndex: 9999,
    },
    
    // Tooltip component with proper sizing and positioning
    tooltip: {
      borderRadius: '6px',
      padding: '8px 12px',
      fontSize: `${TYPOGRAPHY_CONFIG.fontSizes.xs}px`,
      shadow: '0 4px 8px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)',
      transition: '150ms cubic-bezier(0, 0, 0.2, 1)',
      maxWidth: '300px',
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      textColor: '#FFFFFF',
      arrowSize: '6px',
      zIndex: 9000,
      distance: '8px',
      delay: 300,
    },
    
    // Dropdown component with proper sizing and focus management
    dropdown: {
      borderRadius: '8px',
      shadow: '0 4px 12px -2px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      borderColor: 'rgba(0, 0, 0, 0.05)',
      itemPadding: '8px 12px',
      itemHoverBg: 'rgba(179, 231, 44, 0.1)',
      maxHeight: '300px',
      transition: '200ms cubic-bezier(0, 0, 0.2, 1)',
      zIndex: 50,
    },
    
    // Tab component for improved navigation
    tab: {
      borderRadius: '8px',
      padding: '10px 16px',
      fontSize: `${TYPOGRAPHY_CONFIG.fontSizes.sm}px`,
      fontWeight: 500,
      activeBorderColor: '#B3E72C',
      activeBorderWidth: '2px',
      inactiveBorderColor: 'transparent',
      hoverBg: 'rgba(179, 231, 44, 0.05)',
      transition: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
    
    // Badge component for status indicators
    badge: {
      borderRadius: '9999px',
      paddingX: '8px',
      paddingY: '2px',
      fontSize: `${TYPOGRAPHY_CONFIG.fontSizes.xs}px`,
      fontWeight: 500,
      successBg: 'rgba(16, 189, 37, 0.15)',
      successColor: '#097817',
      errorBg: 'rgba(225, 29, 72, 0.15)',
      errorColor: '#9F1239',
      warningBg: 'rgba(245, 158, 11, 0.15)',
      warningColor: '#9A3412',
      infoBg: 'rgba(14, 165, 233, 0.15)',
      infoColor: '#0369A1',
    },
  }
};

// Available themes
export const themes = {
  dark: darkTheme,
  light: lightIntelligenceTheme,
};

// Theme context type
export type ThemeType = 'dark' | 'light';

// Export default theme
export default darkTheme;